import React, { useState, useRef } from 'react';
import { Container } from 'react-bootstrap';
import { motion, useInView, AnimatePresence } from 'framer-motion';

const PREGUNTAS = [
  {
    q: '¿Cuánto tiempo tardará en estar lista mi web?',
    a: 'Depende del tipo de proyecto. Una web sencilla puede estar publicada en 5–7 días laborables. Una web mediana suele tardar entre 10 y 15 días. Una tienda online o un proyecto más complejo puede llevar entre 3 y 5 semanas. Siempre acordamos el plazo antes de empezar y lo cumplimos.',
  },
  {
    q: '¿El dominio y el alojamiento están incluidos?',
    a: 'Sí, todos los planes incluyen el dominio .es y el alojamiento durante el primer año. A partir del segundo año, el coste de renovación es reducido y depende del plan contratado.',
  },
  {
    q: '¿Puedo pedir cambios después de entregar la web?',
    a: 'Por supuesto. Todos los proyectos incluyen revisiones durante el proceso de desarrollo. Una vez entregada la web, ofrecemos soporte y mantenimiento mensual. Los cambios puntuales fuera del contrato se presupuestan aparte a un precio muy ajustado.',
  },
  {
    q: '¿Hacéis webs en valenciano?',
    a: 'Sí, ofrecemos la opción multiidioma (castellano, valenciano e inglés) como servicio adicional. También podemos entregar la web únicamente en valenciano si así lo prefieres. Somos de aquí y entendemos perfectamente esa necesidad.',
  },
  {
    q: '¿Mi web aparecerá en Google?',
    a: 'Todas nuestras webs incluyen optimización para buscadores: estructura de URLs correcta, metaetiquetas, velocidad de carga optimizada y configuración en Google Search Console. Para un posicionamiento más avanzado, disponemos de un servicio específico que puedes contratar como complemento.',
  },
  {
    q: '¿Cómo funciona el proceso de pago?',
    a: 'Solicitamos un 50 % al confirmar el proyecto y el 50 % restante al entregar la web. Aceptamos transferencia bancaria y tarjeta. No hay costes ocultos: el presupuesto firmado es el precio final.',
  },
];

const FAQItem = ({ pregunta, respuesta, index, isOpen, toggle }) => (
  <div
    style={{
      background: 'var(--bg-card)',
      border: `1px solid ${isOpen ? 'rgba(46,139,192,0.4)' : 'rgba(255,255,255,0.06)'}`,
      borderRadius: 'var(--radius)',
      overflow: 'hidden',
      marginBottom: '10px',
      transition: 'border-color 0.2s',
    }}
  >
    <button
      onClick={() => toggle(index)}
      style={{
        width: '100%', textAlign: 'left', background: 'none', border: 'none',
        padding: '18px 22px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: '16px', cursor: 'pointer',
      }}
    >
      <span style={{ color: isOpen ? 'white' : '#c8d8ec', fontWeight: 600, fontSize: '0.95rem', lineHeight: 1.45, fontFamily: 'var(--font-heading)' }}>
        {pregunta}
      </span>
      <div style={{
        flexShrink: 0, width: '30px', height: '30px', borderRadius: '50%',
        border: `2px solid ${isOpen ? '#2e8bc0' : 'rgba(255,255,255,0.15)'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.25s',
      }}>
        <i
          className="fa-solid fa-chevron-down"
          style={{
            color: isOpen ? '#45a8e0' : '#6a86a8',
            fontSize: '0.72rem',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
            transition: 'transform 0.25s',
          }}
        />
      </div>
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          key="content"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.28, ease: 'easeInOut' }}
          style={{ overflow: 'hidden' }}
        >
          <p style={{ color: '#8fa3bf', lineHeight: 1.75, fontSize: '0.91rem', padding: '0 22px 18px', margin: 0 }}>
            {respuesta}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const FAQ = () => {
  const [openIdx, setOpenIdx] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section id="faq" style={{ background: 'var(--bg-surface)' }}>
      <Container>
        <div className="text-center mb-5" ref={ref}>
          <motion.span className="badge-primary mb-3 d-inline-block" initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}}>
            Preguntas frecuentes
          </motion.span>
          <motion.h2 className="section-title" initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.1 }}>
            Dudas habituales
          </motion.h2>
          <div className="accent-line center" />
          <motion.p className="section-subtitle" initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: 0.2 }}>
            Si no encuentras la respuesta que buscas, escríbenos sin ningún compromiso.
          </motion.p>
        </div>

        <div style={{ maxWidth: '780px', margin: '0 auto' }}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ delay: 0.3 }}>
            {PREGUNTAS.map((item, i) => (
              <FAQItem
                key={i} index={i}
                pregunta={item.q} respuesta={item.a}
                isOpen={openIdx === i}
                toggle={(idx) => setOpenIdx(prev => prev === idx ? null : idx)}
              />
            ))}
          </motion.div>

          <motion.div className="text-center mt-4" initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: 0.7 }}>
            <p style={{ color: '#6a86a8', fontSize: '0.88rem' }}>
              ¿Tienes más dudas?{' '}
              <a href="mailto:info@webfacilcastellon.es" style={{ color: '#45a8e0', fontWeight: 600, textDecoration: 'none' }}>
                Escríbenos y te respondemos el mismo día
              </a>
            </p>
          </motion.div>
        </div>
      </Container>
    </section>
  );
};

export default FAQ;
